import React, { useState, useMemo } from 'react';
import {
  Card, CardBody, Col, Row,
} from 'reactstrap';
import ReactTableBase from 'src/shared/components/table/ReactTableBase';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Redirect } from 'react-router';
import { getSubscriptionStatus } from 'src/utils/validationUtils/';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';

const InformapClientDetailsView = ({ reactTableData, isNoRecordFound }) => {
  const rows = reactTableData?.tableRowsData ?? [];
  const { t } = useTranslation('common');
  const tableConfig = {
    isEditable: false,
    isSortable: true,
    isResizable: true,
    withPagination: true,
    withSearchEngine: true,
    manualPageSize: [10, 50, 100, 1000],
    placeholder: 'Search by Client Name, Account No....',
  };

  const [isRedirect, setRedirect] = useState(null);
  let temp;
  const Add = () => {
    setRedirect("register");
  }

  const stats = useMemo(() => {
    const total = rows.length;
    let active = 0;
    let expired = 0;
    let expiringSoon = 0;

    rows.forEach(row => {
      if (row.status === 1) {
        active++;
      }
      const status = getSubscriptionStatus(row.subscriptionEndDate);
      if (status === 'expired') {
        expired++;
      } else if (status === 'expiring-this-month' || status === 'expiring-next-month') {
        expiringSoon++;
      }
    });

    return { total, active, expired, expiringSoon };
  }, [rows]);

  if (isRedirect && isRedirect === "update") {
    return <Redirect to={`/web_app/pnd_Info_client/add/${temp.schemeId}`} />;
  } else if (isRedirect && isRedirect === "register") {
    return <Redirect to={`/web_app/pnd_Info_client/add`} />;
  }

  return (
    <Col md={12} lg={12} >
      {/* Premium Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="mb-1" style={{ fontWeight: 800, color: '#1e293b', fontSize: '1.5rem', letterSpacing: '-0.5px' }}>Informap Client Details</h3>
          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>Manage client accounts, subscription validity, and system configuration.</p>
        </div>
        <button
          onClick={() => Add()}
          style={{
            backgroundColor: '#10b981',
            color: '#ffffff',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '8px',
            fontWeight: 600,
            fontSize: '0.875rem',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 4px 10px rgba(16, 185, 129, 0.2)',
            transition: 'all 0.2s ease',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#059669'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#10b981'; e.currentTarget.style.transform = 'none'; }}
        >
          <AddIcon style={{ fontSize: '1.2rem' }} />
          Add Client
        </button>
      </div>

      {/* Stats Summary Cards Grid */}
      <Row className="mb-4 g-3">
        {/* Total Clients Card */}
        <Col xs={12} sm={6} md={3}>
          <div style={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '16px',
            padding: '20px',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02), 0 2px 4px -1px rgba(0,0,0,0.01)',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            borderLeft: '4px solid #3b82f6'
          }}>
            <div style={{
              backgroundColor: '#eff6ff',
              color: '#3b82f6',
              borderRadius: '12px',
              width: '48px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <PeopleIcon style={{ fontSize: '1.6rem' }} />
            </div>
            <div>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#64748b' }}>Total Clients</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#0f172a', marginTop: '2px' }}>{stats.total}</div>
            </div>
          </div>
        </Col>

        {/* Active Clients Card */}
        <Col xs={12} sm={6} md={3}>
          <div style={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '16px',
            padding: '20px',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02), 0 2px 4px -1px rgba(0,0,0,0.01)',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            borderLeft: '4px solid #10b981'
          }}>
            <div style={{
              backgroundColor: '#ecfdf5',
              color: '#10b981',
              borderRadius: '12px',
              width: '48px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <CheckCircleIcon style={{ fontSize: '1.6rem' }} />
            </div>
            <div>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#64748b' }}>Active Clients</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#0f172a', marginTop: '2px' }}>{stats.active}</div>
            </div>
          </div>
        </Col>

        {/* Expiring Soon Card */}
        <Col xs={12} sm={6} md={3}>
          <div style={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '16px',
            padding: '20px',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02), 0 2px 4px -1px rgba(0,0,0,0.01)',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            borderLeft: '4px solid #f59e0b'
          }}>
            <div style={{
              backgroundColor: '#fffbeb',
              color: '#f59e0b',
              borderRadius: '12px',
              width: '48px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <WarningIcon style={{ fontSize: '1.6rem' }} />
            </div>
            <div>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#64748b' }}>Expiring Soon</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#0f172a', marginTop: '2px' }}>{stats.expiringSoon}</div>
            </div>
          </div>
        </Col>

        {/* Expired Card */}
        <Col xs={12} sm={6} md={3}>
          <div style={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '16px',
            padding: '20px',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02), 0 2px 4px -1px rgba(0,0,0,0.01)',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            borderLeft: '4px solid #ef4444'
          }}>
            <div style={{
              backgroundColor: '#fef2f2',
              color: '#ef4444',
              borderRadius: '12px',
              width: '48px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <ErrorIcon style={{ fontSize: '1.6rem' }} />
            </div>
            <div>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#64748b' }}>Expired</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#0f172a', marginTop: '2px' }}>{stats.expired}</div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Main Table Card */}
      <Card style={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
        <CardBody style={{ padding: '24px' }}>
          {!isNoRecordFound && rows.length > 0 &&
            <ReactTableBase
              name={t('instution_type.modify.name_map')}
              key={'searchable'}
              columns={reactTableData.tableHeaderData}
              data={rows}
              tableConfig={tableConfig}
            />
          }
          {isNoRecordFound && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <p className="text-muted mb-0">No client details found.</p>
            </div>
          )}
        </CardBody>
      </Card>
    </Col>
  );
};

export default InformapClientDetailsView;
